import { watchEffect, nextTick } from "vue";
import { Pointer, pointerWidth, pointerHeight } from "./Pointer";
import { ModalBody } from "./ModalBody";

import { makeNewText } from "@/canvas-api/UserObjects/Text";

import { Position, Side } from "@/types";

HTMLDivElement.prototype.setStyle = function(property: string, value: number | string) {
  switch (property) {
    case "width":
    case "height":
    case "left":
    case "top": {
      this.style[property] = `${value}px`;
      break;
    }
    case "transform": {
      this.style.transform = value as string;
    }
  }
  return this;
};

export class StepManager {
  pointerRef: HTMLDivElement;
  modalBodyRef: HTMLDivElement;
  modalContainerRef: HTMLDivElement;
  currentStep: Step;
  steps: any[];

  constructor(
    pointerRef: HTMLDivElement,
    modalBodyRef: HTMLDivElement,
    modalContainerRef: HTMLDivElement,
    steps: {
      title: string;
      info: string;
      modalBodySide: Side;
      pointer: {
        side: Side;
        centered: boolean;
      };
      reactive: any;
    }[]
  ) {
    this.pointerRef = pointerRef;
    this.modalBodyRef = modalBodyRef;
    this.modalContainerRef = modalContainerRef;

    this.steps = [];

    steps.forEach((step) => {
      this.addStep(step);
    });

    this.currentStep = this.steps[0];
  }

  getLength() {
    return this.steps.length;
  }

  getCurrentStep() {
    return this.currentStep.getStep();
  }

  setCurrentStep(index: number) {
    this.currentStep = this.steps[index];
  }

  addStep(step: {
    title: string;
    info: string;
    modalBodySide: Side;
    pointer: {
      side: Side;
      centered: boolean;
    };
    reactive: any;
  }) {
    this.steps.push(
      new Step(
        this.getLength(),
        step.title,
        step.info,
        new ModalBody(step.modalBodySide, this.modalBodyRef, this.modalContainerRef),
        new Pointer(step.pointer.side, step.pointer.centered, this.pointerRef),
        step.reactive
      )
    );
  }

  async incrementStep() {
    //First panel doesn't use steps
    if (this.currentStep.index < this.getLength() - 1) {
      this.currentStep.removeClone();

      this.currentStep = this.steps[this.currentStep.index + 1];

      this.currentStep.utility();

      this.currentStep.setFire(true);
    }
  }
}

export class Step {
  index: number;
  title: string;
  info: string;
  modalBody: ModalBody;
  pointer: Pointer;
  reactive?: any;
  refClone?: HTMLDivElement;

  constructor(
    index: number,
    title: string,
    info: string,
    modalBody: ModalBody,
    pointer: Pointer,
    reactive?: any
  ) {
    this.index = index;
    this.title = title;
    this.info = info;
    this.modalBody = modalBody;
    this.pointer = pointer;

    if (reactive) {
      this.reactive = reactive;
      this.bindWatcher();
    }
  }

  getStep() {
    return this;
  }

  utility() {
    if (this.index === 3) {
      makeNewText("Enter Text Here", 24, "Open Sans");
    }
  }

  setFire(value: boolean) {
    if (this.reactive) {
      this.reactive.fire = value;
    }
  }

  bindWatcher() {
    watchEffect(
      async () => {
        if (this.reactive.fire && this.reactive.refElement) {
          await nextTick(); //If cloning a larger component, need a moment to let info be set

          this.insertClone(this.reactive.refElement);

          const modalPos = this.getModalPosition(this.reactive.refElement);
          this.modalBody.modalBodyElem
            .setStyle("left", modalPos.left)
            .setStyle("top", modalPos.top);

          const pointPos = this.getPointerPosition(this.reactive.refElement);
          this.pointer.pointerElem.setStyle("left", pointPos.left).setStyle("top", pointPos.top);

          this.reactive.fire = false;
        }
      },
      {
        onTrigger(e) {
          // console.log(e);
        },
        flush: "post",
      }
    );
  }

  insertClone(refElement: HTMLDivElement) {
    const curRefBR = refElement.getBoundingClientRect();

    this.refClone = <HTMLDivElement>refElement.cloneNode(true);

    this.modalBody.modalContainerElem.appendChild(this.refClone);

    this.refClone.style.position = "absolute";
    this.refClone.style.left = `${curRefBR.left}px`;
    this.refClone.style.top = `${curRefBR.top}px`;
    this.refClone.style.width = `${curRefBR.width}px`;
  }

  removeClone() {
    if (this.refClone) {
      this.refClone.remove();
    }
  }

  getModalPosition(refElement: HTMLDivElement): Position {
    switch (this.modalBody.side) {
      case "left": {
        return this.modalBody.getModalBodyPoistionLeft(refElement);
      }
      case "top": {
        return this.modalBody.getModalBodyPoistionTop(refElement);
      }
      case "right": {
        // Need func
        break;
      }
      case "bottom": {
        return this.modalBody.getModalBodyPoistionBottom(refElement);
      }
      case "none": {
        return { left: 0, top: 0 };
      }
    }

    return { left: 0, top: 0 };
  }

  getPointerPosition(refElement: HTMLDivElement): Position {
    switch (this.pointer.side) {
      case "left": {
        if (this.pointer.centered) {
          return this.pointer.getPointerCenetered(this.modalBody.modalBodyElem);
        } else {
          return this.pointer.getPointerPositionLeft(refElement, this.modalBody.modalBodyElem);
        }
      }
      case "top": {
        if (this.pointer.centered) {
          return this.pointer.getPointerCenetered(this.modalBody.modalBodyElem);
        } else {
          return this.pointer.getPointerPositionTop(refElement, this.modalBody.modalBodyElem);
        }
      }
      case "right": {
        if (this.pointer.centered) {
          return this.pointer.getPointerCenetered(this.modalBody.modalBodyElem);
        } else {
          // Need Pointer function
          return { left: pointerWidth / 2, top: pointerHeight / 2 };
        }
      }
      case "bottom": {
        if (this.pointer.centered) {
          return this.pointer.getPointerCenetered(this.modalBody.modalBodyElem);
        } else {
          return this.pointer.getPointerPositionBottom(refElement, this.modalBody.modalBodyElem);
        }
      }
      case "none":
      default: {
        return { left: pointerWidth / 2, top: pointerHeight / 2 };
      }
    }
  }
}
